import React from 'react';
import PropTypes from 'prop-types';
import uuid from 'uuid/v4';
import * as CrosstabUtils from '../../../util/CrosstabUtils';

import downArrowIconRed from '../../../images/down-arrow-icon_red.png';
import upArrowIconGreen from '../../../images/up-arrow-icon_green.png';
import flatIconYellowIndex from '../../../images/flat-icon-yellow.png';

const getConditionalHighlighting = (
  ElementKey,
  verticalData,
  horizontalData,
) => {
  if (
    verticalData &&
    (ElementKey === 'sample' ||
      ElementKey === 'weighted' ||
      ElementKey === 'vertical')
  ) {
    const vertical = Math.round(Number(verticalData.replace(/[%|,|*]/g, '')));
    const backgroundColor = () => {
      if (ElementKey === 'sample') {
        return '#FCDAD6';
      } else if (ElementKey === 'weighted') {
        return '#CFE4F9';
      }
      return '#d8efb8';
    };
    return `-webkit-linear-gradient(left, ${backgroundColor()} ${vertical}%, white ${vertical}%)`;
  } else if (horizontalData && ElementKey === 'horizontal') {
    const horizontal = Math.round(
      Number(horizontalData.replace(/[%|,|*]/g, '')),
    );
    return `-webkit-linear-gradient(left, #ffedb1 ${horizontal}%, white ${horizontal}%)`;
  }
  return '';
};

const getIndexSymbol = (key, value) => {
  if (value && key === 'index') {
    const index = Number(value.replace(/[%|,|*]/g, ''));
    if (index > 110) {
      return (
        <b>
          {value}
          <img
            src={upArrowIconGreen}
            alt="Up Arrow"
            className="upArrowIconBlue"
          />
        </b>
      );
    } else if (index < 90 && index !== 0) {
      return (
        <span>
          {value}
          <img
            src={downArrowIconRed}
            alt="Up Arrow"
            className="downArrowIconBlue"
          />
        </span>
      );
    } else if (index >= 90 && index <= 110) {
      return (
        <span>
          {value}
          <img
            src={flatIconYellowIndex}
            alt="Flat"
            className="downArrowIconBlue"
          />
        </span>
      );
    }
    return <span className="float-left">{value}</span>;
  }
  return value;
};

const CrosstabViewerGridBodyCell = ({
  viewerGridType,
  cellElementList,
  cellData,
  privateEyeCellElement,
  cellDataList,
  filterType,
  hnf,
  cellWidth,
  isColumnEnd,
}) => {
  const highlightAndFilterStyles = {};
  const textColor = {};
  const backgroundColor = {};

  const calculateHighlightAndFilterStyles = (elementVal, cellElement) => {
    for (const x of hnf[cellElement.key].filter) {
      if (
        x.operator === 'Greater Than' &&
        cellElement.compute(elementVal) > Number(x.value)
      ) {
        highlightAndFilterStyles.color = 'transparent !important';
        highlightAndFilterStyles.opacity = 0;
      } else if (
        x.operator === 'Less Than' &&
        cellElement.compute(elementVal) < Number(x.value)
      ) {
        highlightAndFilterStyles.color = 'transparent !important';
        highlightAndFilterStyles.opacity = 0;
      } else if (
        x.operator === 'Equals' &&
        cellElement.compute(elementVal) === Number(x.value)
      ) {
        highlightAndFilterStyles.color = 'transparent !important';
        highlightAndFilterStyles.opacity = 0;
      } else if (
        x.operator === 'Range' &&
        Number(x.value.split(' to ')[0]) <= cellElement.compute(elementVal) &&
        cellElement.compute(elementVal) <= Number(x.value.split(' to ')[1])
      ) {
        highlightAndFilterStyles.color = 'transparent !important';
        highlightAndFilterStyles.opacity = 0;
      }
    }
    for (const i of hnf[cellElement.key].highlight) {
      if (
        i.operator === 'Greater Than' &&
        cellElement.compute(elementVal) > Number(i.value)
      ) {
        if (i.textColor) {
          textColor[cellElement.key] = i.textColor;
        }
        if (i.cellColor) {
          backgroundColor[cellElement.key] = i.cellColor;
        }
      } else if (
        i.operator === 'Less Than' &&
        cellElement.compute(elementVal) < Number(i.value)
      ) {
        if (i.textColor) {
          textColor[cellElement.key] = i.textColor;
        }
        if (i.cellColor) {
          backgroundColor[cellElement.key] = i.cellColor;
        }
      } else if (
        i.operator === 'Equals' &&
        cellElement.compute(elementVal) === Number(i.value)
      ) {
        if (i.textColor) {
          textColor[cellElement.key] = i.textColor;
        }
        if (i.cellColor) {
          backgroundColor[cellElement.key] = i.cellColor;
        }
      } else if (
        i.operator === 'Range' &&
        Number(i.value.split(' to ')[0]) <= cellElement.compute(elementVal) &&
        cellElement.compute(elementVal) <= Number(i.value.split(' to ')[1])
      ) {
        if (i.textColor) {
          textColor[cellElement.key] = i.textColor;
        }
        if (i.cellColor) {
          backgroundColor[cellElement.key] = i.cellColor;
        }
      }
    }
  };

  if (cellData === CrosstabUtils.NOT_VALID_LABEL) {
    return (
      <td key={uuid()} style={{ width: cellWidth }}>
        <div>{CrosstabUtils.NOT_VALID_LABEL}</div>
      </td>
    );
  }

  if (filterType === 'HNF' && hnf) {
    if (
      viewerGridType === CrosstabUtils.CROSSTAB_VIEWER_GRID_TYPE.PRIVATE_EYE
    ) {
      calculateHighlightAndFilterStyles(cellData, privateEyeCellElement);
    } else {
      cellElementList.map(item => {
        if (
          viewerGridType !== CrosstabUtils.CROSSTAB_VIEWER_GRID_TYPE.TREND ||
          item.isTrendVisibility
        ) {
          calculateHighlightAndFilterStyles(cellData[item.key], item);
        }
        return false;
      });
    }
  } else if (
    filterType === 'DefaultFilter' &&
    viewerGridType === CrosstabUtils.CROSSTAB_VIEWER_GRID_TYPE.PRIVATE_EYE
  ) {
    highlightAndFilterStyles.background = getConditionalHighlighting(
      privateEyeCellElement.key,
      cellDataList.vertical,
      cellDataList.horizontal,
    );
  } else {
    highlightAndFilterStyles.background = 'transparent';
    highlightAndFilterStyles.color = '#171717';
  }

  if (viewerGridType === CrosstabUtils.CROSSTAB_VIEWER_GRID_TYPE.PRIVATE_EYE) {
    return (
      <td
        key={uuid()}
        style={{ width: cellWidth }}
        className={isColumnEnd ? 'column-end' : ''}
      >
        <div
          className="clearfix Abhipsa"
          style={{
            ...highlightAndFilterStyles,
            backgroundColor:
              backgroundColor[privateEyeCellElement.key] || 'transparent',
            color: textColor[privateEyeCellElement.key] || '#171717',
          }}
          title={cellData}
        >
          {filterType === 'HNF' && hnf ? (
            <span className="float-left">{cellData}</span>
          ) : (
            getIndexSymbol(privateEyeCellElement.key, cellData)
          )}
        </div>
      </td>
    );
  }

  const el = cellElementList.reduce((acc, item) => {
    const textCol = {};
    const backCol = {};
    if (filterType === 'DefaultFilter') {
      highlightAndFilterStyles.background = getConditionalHighlighting(
        item.key,
        cellData.vertical,
        cellData.horizontal,
      );
    }
    if (!highlightAndFilterStyles.opacity) {
      textCol.color = textColor[item.key] ? textColor[item.key] : '#171717';
      backCol.backgroundColor = backgroundColor[item.key]
        ? backgroundColor[item.key]
        : 'transparent';
    }

    if (
      item.isVisible &&
      (viewerGridType !== CrosstabUtils.CROSSTAB_VIEWER_GRID_TYPE.TREND ||
        item.isTrendVisibility)
    ) {
      return [
        ...acc,
        <div
          style={{ ...textCol, ...backCol, ...highlightAndFilterStyles }}
          key={uuid()}
        >
          {getIndexSymbol(item.key, cellData[item.key])}
        </div>,
      ];
    }
    return acc;
  }, []);
  return (
    <td key={uuid()} style={{ width: cellWidth }}>
      {el}
    </td>
  );
};

CrosstabViewerGridBodyCell.propTypes = {
  cellElementList: PropTypes.arrayOf(PropTypes.shape()),
  viewerGridType: PropTypes.oneOf(
    Object.values(CrosstabUtils.CROSSTAB_VIEWER_GRID_TYPE),
  ),
  cellData: PropTypes.shape({}),
  privateEyeCellElement: PropTypes.shape({}),
  cellDataList: PropTypes.shape({}),
  filterType: PropTypes.string,
  hnf: PropTypes.shape({}),
  cellWidth: PropTypes.number,
  isColumnEnd: PropTypes.bool,
};

export default CrosstabViewerGridBodyCell;
